import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { DemoEndFeedbackRequest, DemoEndFeedbackResponse } from '../types';
import './DemoCompletedPage.css';

interface DemoCompletedPageProps {
  sessionId: string;
  onOpenSurvey: (origin: 'demo-end' | 'feedback-button', demoEndAnswers?: { wantToContinue: boolean; wantToGiveFeedback: boolean }) => void;
}

const DemoCompletedPage: React.FC<DemoCompletedPageProps> = ({ sessionId, onOpenSurvey }) => {
  const [wantToContinue, setWantToContinue] = useState<boolean | null>(null);
  const [wantToGiveFeedback, setWantToGiveFeedback] = useState<boolean | null>(null);
  const [showFinalThankYou, setShowFinalThankYou] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (wantToContinue === null || wantToGiveFeedback === null) {
      alert('Please answer both questions before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData: DemoEndFeedbackRequest = {
        session_id: sessionId,
        want_to_continue: wantToContinue,
        want_to_give_feedback: wantToGiveFeedback
      };

      await axios.post<DemoEndFeedbackResponse>(`${API_URL}/feedback/demo-end/`, requestData);

      // If user answered yes to either question, open survey modal
      if (wantToContinue || wantToGiveFeedback) {
        onOpenSurvey('demo-end', { wantToContinue, wantToGiveFeedback });
      } else {
        // Both are No - show final thank you
        setShowFinalThankYou(true);
      }
    } catch (error) {
      console.error('Error submitting demo-end feedback:', error);
      alert('There was an error submitting your feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showFinalThankYou) {
    return (
      <div className="demo-completed-content">
        <div className="thank-you-card final">
          <h2>Thank you for playing and for your feedback. It made our day!</h2>
          <p>A game is just a bunch of random code on a computer, until someone plays it. You added the magic.</p>
          <p className="closing-message">
            More adventures with Percy and Alfie coming soon. Tell your friends (if you liked the game. If you hated it, tell your enemies!)
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-completed-content">
      <div className="demo-completed-card">
        <div className="demo-completed-header">
          <h2>Thanks for playing The Secret Society of Time Traveling Cats!</h2>
          <p className="subtitle">Percy and Alfie appreciate your help testing their adventure!</p>
        </div>

        <div className="demo-completed-questions">
          <div className="question-section">
            <p className="question-text">Would you like to continue playing when we fix some bugs and add more adventure?</p>
            <div className="radio-group">
              <label className={`radio-option ${wantToContinue === true ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="want_to_continue"
                  checked={wantToContinue === true}
                  onChange={() => setWantToContinue(true)}
                />
                <span>Yes</span>
              </label>
              <label className={`radio-option ${wantToContinue === false ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="want_to_continue"
                  checked={wantToContinue === false}
                  onChange={() => setWantToContinue(false)}
                />
                <span>No</span>
              </label>
            </div>
          </div>

          <div className="question-section">
            <p className="question-text">Would you like to share feedback with our team?</p>
            <div className="radio-group">
              <label className={`radio-option ${wantToGiveFeedback === true ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="want_to_give_feedback"
                  checked={wantToGiveFeedback === true}
                  onChange={() => setWantToGiveFeedback(true)}
                />
                <span>Yes</span>
              </label>
              <label className={`radio-option ${wantToGiveFeedback === false ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="want_to_give_feedback"
                  checked={wantToGiveFeedback === false}
                  onChange={() => setWantToGiveFeedback(false)}
                />
                <span>No</span>
              </label>
            </div>
          </div>
        </div>

        <div className="demo-completed-actions">
          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={wantToContinue === null || wantToGiveFeedback === null || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoCompletedPage;
